import type { LinkProps as NextLinkProps } from 'next/link';

import { forwardRef } from 'react';
import NextLink from 'next/link';
import { twMerge } from 'tailwind-merge';

import { ExternalLinkIcon } from '@paid-ui/icons/external-link';

export interface LinkProps
  extends NextLinkProps,
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  external?: boolean;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href, target, external, hidden, className, children, ...restProps }, ref) => {
    if (hidden) {
      return null;
    }

    if (!href) {
      console.warn('[Link] - Link component requires an valid `href`.');

      return (
        <a
          ref={ref}
          className={twMerge(
            'inline-flex font-medium text-grey-dark no-underline outline-none ring-2 ring-pink ring-offset-2',
            className,
          )}
          {...restProps}
        >
          {children}
        </a>
      );
    }

    return (
      <NextLink
        ref={ref}
        href={href}
        target={target}
        className={twMerge(
          'group items-center font-medium text-blue underline-offset-2 outline-none transition-colors duration-150 ease-linear hover:text-blue-dark hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue/50',
          external ? 'inline-flex items-center gap-1' : '',
          className,
        )}
        {...restProps}
      >
        {external ? (
          <>
            <span>{children}</span>
            <ExternalLinkIcon className="opacity-50 group-hover:opacity-100" />
          </>
        ) : (
          children
        )}
      </NextLink>
    );
  },
);

Link.displayName = 'Link';
